"use client";

import { AuraPresence } from "../aura-presence";
import { Button } from "@/components/ui/button";
import type { OperationalStateContract } from "@/src/data/operational-state";
import { ArrowLeft, CheckCircle2, FileSearch } from "lucide-react";

interface FutureOperationalSurfaceProps {
  surface: OperationalStateContract["futureSurfaces"][number];
  onBack: () => void;
  onOpenDecisionRoom: () => void;
  onCommandAura: () => void;
  onAction?: (action: string) => void;
}

export function FutureOperationalSurface({
  surface,
  onBack,
  onOpenDecisionRoom,
  onCommandAura,
  onAction,
}: FutureOperationalSurfaceProps) {
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-5xl space-y-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Operational Console
        </button>

        <section className="glass-panel rounded-xl p-6">
          <div className="flex items-start justify-between gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <AuraPresence size="sm" intensity="subtle" />
                <h1 className="text-xl font-semibold text-foreground">
                  {surface.title}
                </h1>
              </div>
              <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
                {surface.description}
              </p>
            </div>
            <div className="rounded-lg border border-accent/20 bg-accent/10 px-4 py-3 text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Current State
              </div>
              <div className="mt-1 text-sm font-medium text-accent">
                {surface.primaryMetric}
              </div>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-[1fr_280px] gap-6">
          <section className="glass-panel rounded-xl p-5">
            <div className="mb-4 flex items-center gap-2">
              <FileSearch className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Evidence Available
              </h2>
            </div>
            <div className="space-y-2">
              {surface.evidenceItems.map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-2 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-foreground"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <aside className="glass-panel rounded-xl p-5">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Available Actions
            </h2>
            <div className="space-y-2">
              {surface.actions.map((action) => (
                <Button
                  key={action}
                  variant="outline"
                  className="w-full justify-start text-sm"
                  onClick={
                    action.includes("Decision")
                      ? onOpenDecisionRoom
                      : action.includes("AURA")
                        ? onCommandAura
                        : () => onAction?.(action)
                  }
                >
                  {action}
                </Button>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
